import { Component, Input } from '@angular/core';

type FooterLink = { label: string; href: string; target?: '_blank'; sectionId?: string; walkthroughStep?: number };

type FooterGroup = {
  title: string;
  links: FooterLink[];
};

@Component({
  selector: 'app-home-footer',
  standalone: false,
  templateUrl: './home-footer.component.html',
  styleUrl: './home-footer.component.scss',
})
export class HomeFooterComponent {
  currentYear = new Date().getFullYear();
  @Input() isWhiteText: boolean = false;
  email = '';
  submitted = false;

  groups: FooterGroup[] = [
    {
      title: 'Platform',
      links: [
        { label: 'Features', href: '#features', sectionId: 'features' },
        { label: 'Coverage Map', href: '#coverage', sectionId: 'coverage' },
        { label: 'Reporting & Analytics', href: '#walkthrough', sectionId: 'walkthrough', walkthroughStep: 3 },
        { label: 'API Reference', href: 'https://developer.firstorion.com/', target: '_blank' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About First Orion', href: 'https://firstorion.com/about-us', target: '_blank' },
        { label: 'Careers', href: 'https://firstorion.com/careers', target: '_blank' },
        { label: 'Press', href: 'https://firstorion.com/resources/press-releases', target: '_blank' },
        { label: 'Contact', href: 'https://firstorion.com/contact-us', target: '_blank' },
      ],
    },
  ];

  social: { label: string; href: string; icon: 'linkedin' | 'twitter' | 'youtube' | 'facebook' }[] = [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/firstorion', icon: 'linkedin' },
    { label: 'X / Twitter', href: 'https://twitter.com/firstorioncorp', icon: 'twitter' },
    { label: 'YouTube', href: 'https://www.youtube.com/c/firstorion', icon: 'youtube' },
    { label: 'Facebook', href: 'https://www.facebook.com/firstorioncorp', icon: 'facebook' },
  ];

  submit(): void {
    if (!this.email) return;
    this.submitted = true;
  }

  openPrivacyChoices(event: Event) {
    event.preventDefault();
    if (window._iub && window._iub.cs && window._iub.cs.api) {
      window._iub.cs.api.openPreferences();
    }
  }

  onFooterLinkClick(event: Event, link: FooterLink): void {
    if (!link.sectionId) {
      return;
    }

    event.preventDefault();

    if (typeof link.walkthroughStep === 'number') {
      window.dispatchEvent(new CustomEvent<number>('icip-walkthrough-step', { detail: link.walkthroughStep }));
    }

    const target = document.getElementById(link.sectionId);
    if (!target) {
      return;
    }

    const headerOffset = 72;
    const startPosition = window.scrollY;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    const distance = targetPosition - startPosition;
    const duration = 900;
    const startTime = performance.now();

    const easeInOutCubic = (progress: number): number =>
      progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    const animateScroll = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));

      if (progress < 1) {
        window.requestAnimationFrame(animateScroll);
      } else {
        history.replaceState(null, '', link.href);
      }
    };

    window.scrollTo(0, startPosition + distance * easeInOutCubic(0.02));
    window.requestAnimationFrame(animateScroll);
  }
}
